/**
 * Highlights search terms in text by wrapping matches in <mark> tags
 * @param {string} text - The text to search and highlight
 * @param {string} searchText - The search term(s) to highlight
 * @returns {string} - Text with highlighted matches wrapped in <mark> tags
 */
export const highlightSearchText = (text, searchText) => {
    if (!text || !searchText) {
        return text;
    }

    // Split search text into individual keywords and clean them
    const keywords = searchText
        .trim()
        .split(/\s+/)
        .filter((keyword) => keyword.length > 0);

    if (keywords.length === 0) {
        return text;
    }

    let highlightedText = text;

    // Process each keyword
    keywords.forEach((keyword) => {
        // Escape special regex characters in the keyword
        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        // Create regex for case-insensitive global matching
        const regex = new RegExp(`(${escapedKeyword})`, 'gi');

        // Replace matches with highlighted version
        highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
    });

    return highlightedText;
};

/**
 * Highlights search terms in a review object
 * @param {Object} review - The review object
 * @param {string} searchText - The search term(s) to highlight
 * @returns {Object} - Review object with highlighted comment field
 */
export const highlightReviewText = (review, searchText) => {
    if (!review || !searchText) {
        return review;
    }

    return {
        ...review,
        comment: highlightSearchText(review.comment, searchText),
    };
};

/**
 * Highlights search terms in an array of reviews
 * @param {Array} reviews - Array of review objects
 * @param {string} searchText - The search term(s) to highlight
 * @returns {Array} - Array of review objects with highlighted comment fields
 */
export const highlightReviews = (reviews, searchText) => {
    if (!Array.isArray(reviews) || !searchText) {
        return reviews;
    }

    return reviews.map((review) => highlightReviewText(review, searchText));
};
